import { useCallback } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ValuesVisibilityState } from '../types';
import { readCookie, writeCookie } from '../utils/cookie';

export const STORAGE_KEY = 'values-visibility';

export interface UseValuesVisibility {
  valuesVisibility: ValuesVisibilityState;
  toggle: () => void;
  set: (value: ValuesVisibilityState) => void;
}

function isValuesVisibilityState(value: string | null | undefined): value is ValuesVisibilityState {
  return value === 'visible' || value === 'hidden';
}

function getCurrentValuesVisibility(): ValuesVisibilityState {
  const cookieValue = readCookie(STORAGE_KEY);

  return isValuesVisibilityState(cookieValue) ? cookieValue : 'visible';
}

export function useValuesVisibility(): UseValuesVisibility {
  const queryClient = useQueryClient();

  const { data } = useQuery<ValuesVisibilityState>({
    queryKey: [STORAGE_KEY],
    queryFn: getCurrentValuesVisibility,
    staleTime: Infinity,
    initialData: getCurrentValuesVisibility
  });

  const set = useCallback(
    (value: ValuesVisibilityState): void => {
      writeCookie(STORAGE_KEY, value);
      queryClient.setQueryData<ValuesVisibilityState>([STORAGE_KEY], value);
    },
    [queryClient]
  );

  const toggle = useCallback((): void => {
    queryClient.setQueryData<ValuesVisibilityState>([STORAGE_KEY], (currentValue): ValuesVisibilityState => {
      const currentState = currentValue || getCurrentValuesVisibility();
      const newValue = currentState === 'visible' ? 'hidden' : 'visible';

      writeCookie(STORAGE_KEY, newValue);

      return newValue;
    });
  }, [queryClient]);

  return {
    valuesVisibility: data || 'visible',
    toggle,
    set
  };
}
